import { useBoolean, useNotify } from '@aibees/shared/hooks';
/* eslint-disable no-unsafe-optional-chaining */
import { useBrowseEnrichmentApi } from '@aibees/elephant/api/enrichment/use-browse-enrichment-api';
import { useCreateEnrichmentMutationApi } from '@aibees/elephant/api/enrichment/use-create-enrichment-mutation-api';
import { ExportTemplatePresetValue } from '@aibees/elephant/components/export-dialog';
import { EnrichmentContent, EnrichmentTypeEnum } from '@aibees/elephant/types';
import { Label } from '@aibees/elephant/types/label';
import {
  EnrichmentFolder,
  EnrichmentMode,
  EnrichmentOptionTypeEnum,
  ParsedPreset,
  Preset,
  UserRelation,
} from '@aibees/shared/definition';
import { EXPORT_SIZE_LIMIT } from '@aibees/shared/features/advance-table/components/advanced-table-select-popup/constant';
import { useWorkspace } from '@aibees/shared/workspace';
import { useQueryClient } from '@tanstack/react-query';
import { random } from 'lodash-es';
import { ParseResult } from 'papaparse';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useConvertEnrichmentDataToExportSetting } from '../hooks/use-convert-enrichment-data-to-export-setting';
import {
  CREATE_DIALOG_TYPE,
  CreateEnrichmentContextData as ContextData,
  CreateEnrichmentContextProps as ContextProps,
  RowData,
} from './types';

export const ENRICHMENT_TYPE_LABELS = {
  [EnrichmentTypeEnum.ALL]: 'All',
  [EnrichmentTypeEnum.COMPANY]: 'Company',
  [EnrichmentTypeEnum.PERSON]: 'People',
  [EnrichmentTypeEnum.EMAIL]: 'Email',
  [EnrichmentTypeEnum.EMAIL_FINDER]: 'Email Finder',
  [EnrichmentTypeEnum.EMAIL_VERIFIER]: 'Email Verifier',
  [EnrichmentMode.AI_PERSONALITY]: 'Personality',
} as const;

const context = createContext<ContextData>({} as ContextData);

export const { Provider } = context;

export const useCreateEnrichmentContext = () => useContext(context);

export const CreateEnrichmentProvider = ({ children }: ContextProps) => {
  const [dialogType, setDialogType] = useState<CREATE_DIALOG_TYPE | null>(null);
  const [previousDialogType, setPreviousDialogType] =
    useState<CREATE_DIALOG_TYPE | null>(null);
  const [enrichmentType, setEnrichmentType] = useState<
    EnrichmentTypeEnum | undefined
  >(undefined);
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const [selectedColumn, setSelectedColumn] = useState<string>('');

  const [csvData, setCsvData] = useState<ParseResult<RowData>>();
  const [creditCost, setCreditCost] = useState<number>(0);
  const [creditUsageRate, setCreditUsageRate] = useState<number>(0);
  const [templateName, setTemplateName] = useState<string>('');
  const [item, setItem] = useState<Pick<Preset, 'id' | 'refId'> | undefined>();
  const [folder, setFolder] = useState<EnrichmentFolder | undefined>();
  const [isEditTemplate, setIsEditTemplate] = useState<boolean>(false);
  const [isEditFolder, setIsEditFolder] = useState<boolean>(false);
  const [mappedCsvData, setMappedCsvData] = useState<EnrichmentContent[]>([]);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] =
    useState<ParsedPreset<ExportTemplatePresetValue> | null>(null);
  const [activeOptions, setActiveOptions] = useState<
    EnrichmentOptionTypeEnum[]
  >([]);
  const [overwriteOwner, setOverwriteOwner] = useState<boolean>(false);

  const [selectedLabels, setSelectedLabels] = useState<Label[]>([]);
  const [selectedLists, setSelectedLists] = useState<Label[]>([]);
  const exportTemplateDialog = useBoolean(false);
  const exportDeleteTemplateDialog = useBoolean(false);
  const exportDeleteFolderDialog = useBoolean(false);
  const exportAddFolderDialog = useBoolean(false);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const currentUser = useWorkspace()?.user;
  const [selectedUser, setSelectedUser] = useState<UserRelation | undefined>(
    currentUser
  );

  const { mutateAsync: createEnrichment, isLoading: isCreatingEnrichment } =
    useCreateEnrichmentMutationApi();

  // Get export setting based on selected template
  const exportSetting = useConvertEnrichmentDataToExportSetting({
    selectedTemplateName: selectedTemplate?.name,
    enrichmentType: enrichmentType,
  });
  const handleConfirmEnrich = () => {
    if (!enrichmentType) return;

    const options =
      enrichmentType === EnrichmentTypeEnum.PERSON ? activeOptions : [];
    const allLabels = [...selectedLabels, ...selectedLists];

    createEnrichment({
      content: mappedCsvData,
      type: enrichmentType,
      name: uploadedFileName,
      options,
      labels: allLabels.map((label) => label.id),
      template: {
        name: selectedTemplate?.name || 'Default ExportTemplates',
        setting: exportSetting || {},
      },
      overwriteOwner,
      user: selectedUser || {
        id: '',
        username: '',
        firstName: '',
        lastName: '',
      },
    }).then(() => {
      handleCloseDialog();
      // Invalidate browse enrichment query to refresh the list
      queryClient.invalidateQueries({
        queryKey: [useBrowseEnrichmentApi.name],
      });
      notify.complete(
        'Enrichment Started',
        'Your enrichment process has started successfully.',
        random(1, 101),
        {
          icon: 'view-in-ar',
        }
      );
    });
  };

  const canConfirmEnrich = (): boolean => {
    return mappedCsvData.length > 0;
  };

  const handleOpenDialog = (type: CREATE_DIALOG_TYPE) => {
    setDialogType(type);
  };

  const handleOpenExitConfirmation = () => {
    if (
      dialogType === CREATE_DIALOG_TYPE.PREVIEW_DATA ||
      dialogType === CREATE_DIALOG_TYPE.CONFIRM_ENRICH
    ) {
      setPreviousDialogType(dialogType);
      setDialogType(CREATE_DIALOG_TYPE.EXIT_CONFIRMATION);
    }
  };

  const handleCancelExit = () => {
    if (previousDialogType) {
      setDialogType(previousDialogType);
      setPreviousDialogType(null);
    }
  };

  const handleSetTemplateName = (name: string) => {
    setTemplateName(name);
  };

  const handleSetItem = (i: Pick<Preset, 'id' | 'refId'> | undefined) => {
    setItem(i);
  };

  const handleSetTemplateDialogMode = (mode: boolean) => {
    setIsEditTemplate(mode);
  };

  const handleSetFolderDialogMode = (mode: boolean) => {
    setIsEditFolder(mode);
  };

  const handleSetFolder = (enrichmentFolder: EnrichmentFolder | undefined) => {
    setFolder(enrichmentFolder);
  };

  const handleCloseDialog = () => {
    setDialogType(null);
    setSelectedFile(null);
    setUploadedFileName('');
  };

  const handleSelectEnrichmentType = (type: EnrichmentTypeEnum) => {
    setEnrichmentType(type);
  };

  const handleSelectFile = (file: any) => {
    setSelectedFile(file);
    if (file?.name) {
      setUploadedFileName(file.name);
    }
  };

  const handleSelectColumn = (column: string) => {
    setSelectedColumn(column);
  };

  const handleToggleOption = (
    optionType: EnrichmentOptionTypeEnum,
    isActive: boolean
  ) => {
    setActiveOptions((prev) => {
      if (isActive) {
        // Add option if not already present
        return prev.includes(optionType) ? prev : [...prev, optionType];
      } else {
        // Remove option
        return prev.filter((type) => type !== optionType);
      }
    });
  };

  const handleSetOverwriteOwner = (value: boolean) => {
    setOverwriteOwner(value);
  };

  const handleSetUser = (user: UserRelation | undefined) => {
    setSelectedUser(user);
  };

  const handleSetLabels = (labels: Label[]) => {
    setSelectedLabels(labels);
  };

  const handleSetLists = (lists: Label[]) => {
    setSelectedLists(lists);
  };

  const csvDataRows =
    (csvData?.data?.length || 0) - (csvData?.errors?.length || 0);

  const rowsLimitReached = useMemo(
    () => csvDataRows > EXPORT_SIZE_LIMIT,
    [csvDataRows]
  );

  useEffect(() => {
    setCreditCost(creditUsageRate * csvDataRows);
  }, [creditUsageRate, csvDataRows]);

  useEffect(() => {
    setMappedCsvData([]);
  }, [selectedFile]);

  return (
    <Provider
      value={{
        dialogType,
        previousDialogType,
        handleOpenDialog,
        handleOpenExitConfirmation,
        handleCancelExit,
        handleCloseDialog,
        enrichmentType,
        handleSelectEnrichmentType,
        selectedFile,
        handleSelectFile,
        selectedColumn,
        handleSelectColumn,
        csvData,
        setCsvData,
        csvDataRows,
        creditCost,
        isEditTemplate,
        setCreditCost,
        templateName,
        creditUsageRate,
        setCreditUsageRate,
        exportTemplateDialog,
        exportDeleteTemplateDialog,
        exportDeleteFolderDialog,
        exportAddFolderDialog,
        handleSetTemplateName,
        handleSetTemplateDialogMode,
        folder,
        handleSetFolder,
        handleSetFolderDialogMode,
        isEditFolder,
        handleSetItem,
        item,
        rowsLimitReached,
        mappedCsvData,
        setMappedCsvData,
        uploadedFileName,
        setUploadedFileName,
        handleConfirmEnrich,
        canConfirmEnrich: canConfirmEnrich(),
        selectedTemplate,
        setSelectedTemplate,
        exportSetting,
        activeOptions,
        handleToggleOption,
        isCreatingEnrichment,
        overwriteOwner,
        handleSetOverwriteOwner,
        selectedUser,
        handleSetUser,
        selectedLabels,
        handleSetLabels,
        selectedLists,
        handleSetLists,
      }}
    >
      {children}
    </Provider>
  );
};
